// "Altri dettagli", the optional fields behind a disclosure: ingredients as chips, added one by one or pasted as a
// list (ITM-07, EDT-06), a photo's address (ITM-05), the cold chain as one choice (ITM-08), and for drinks the wine's
// producer and origin (ITM-11). Text saves when it loses focus, a choice at once.
import { useEffect, useRef, useState, type ClipboardEvent } from "react";
import { CloseIcon } from "@/components/icons";
import { splitIngredients } from "../rules";
import type { Cold, ItemPatch, MenuItem, Wine } from "../types";
import { BlurText, Field, FieldError, Section, Segmented, fieldClass, type SegmentOption } from "./drawerFields";

type ColdChoice = Cold | "none";
const COLD: SegmentOption<ColdChoice>[] = [
  { value: "none", label: "Nessuno" },
  { value: "frozen", label: "Congelato" },
  { value: "chilled", label: "Abbattuto/Refrigerato" },
  { value: "defrosted", label: "Decongelato" },
];

const WINE_FIELDS: { key: keyof Wine; label: string; wide?: boolean }[] = [
  { key: "producer", label: "Produttore", wide: true },
  { key: "place", label: "Località" },
  { key: "region", label: "Regione" },
  { key: "country", label: "Paese" },
  { key: "grape", label: "Vitigno" },
  { key: "vintage", label: "Annata" },
];
const NO_WINE: Wine = { producer: "", place: "", region: "", country: "", grape: "", vintage: "" };

/** The wine with one field changed; a wine whose every field is empty is no wine at all. */
export function withWineField(wine: Wine | null, key: keyof Wine, text: string): Wine | null {
  const next = { ...(wine ?? NO_WINE), [key]: text };
  return Object.values(next).every((v) => v.trim() === "") ? null : next;
}

function IngredientsEditor({ ingredients, disabled, onSave }: { ingredients: string[]; disabled: boolean; onSave: (next: string[]) => void }) {
  const [draft, setDraft] = useState("");
  const latest = useRef({ draft, ingredients, onSave });
  latest.current = { draft, ingredients, onSave };
  // A word typed and never confirmed is kept when the drawer closes or moves on.
  useEffect(() => () => {
    const { draft: text, ingredients: list, onSave: save } = latest.current;
    const more = splitIngredients(text).filter((s) => !list.some((i) => i.toLowerCase() === s.toLowerCase()));
    if (more.length > 0) save([...list, ...more]);
  }, []);

  const add = (text: string) => {
    const more = splitIngredients(text).filter((s, n, all) =>
      !ingredients.some((i) => i.toLowerCase() === s.toLowerCase()) && all.findIndex((o) => o.toLowerCase() === s.toLowerCase()) === n);
    latest.current.draft = "";
    setDraft("");
    if (more.length > 0) onSave([...ingredients, ...more]);
  };
  const paste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");
    if (!/[,\n]/.test(text)) return;
    e.preventDefault();
    add(`${draft},${text}`);
  };

  return (
    <div>
      {ingredients.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-1.5" aria-label="Ingredienti">
          {ingredients.map((name, n) => (
            <li
              // eslint-disable-next-line react/no-array-index-key -- the same word may appear twice in stored data
              key={`${name}-${n}`}
              className="inline-flex min-h-[30px] items-center gap-1 rounded-full bg-gray-100 pl-3 pr-1 text-[13px] text-gray-800"
            >
              {name}
              <button
                type="button"
                disabled={disabled}
                onClick={() => onSave(ingredients.filter((_, k) => k !== n))}
                aria-label={`Togli ${name}`}
                className="grid h-[24px] w-[24px] place-items-center rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-50"
              >
                <CloseIcon className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        type="text"
        value={draft}
        disabled={disabled}
        enterKeyHint="enter"
        onChange={(e) => setDraft(e.target.value)}
        onPaste={paste}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            add(draft);
          }
        }}
        onBlur={() => add(draft)}
        placeholder="Aggiungi, o incolla un elenco"
        aria-label="Aggiungi ingrediente"
        className={fieldClass}
      />
    </div>
  );
}

interface Props {
  item: MenuItem;
  disabled: boolean;
  errors: Partial<Record<"ingredients" | "photoUrl" | "cold" | "wine", string>>;
  onSave: (field: "ingredients" | "photoUrl" | "cold" | "wine", patch: ItemPatch) => void;
}

export function OtherDetails({ item, disabled, errors, onSave }: Props) {
  const wine = item.wine;
  return (
    <div className="space-y-6">
      <Section title="Ingredienti">
        <IngredientsEditor ingredients={item.ingredients} disabled={disabled} onSave={(ingredients) => onSave("ingredients", { ingredients })} />
        <FieldError message={errors.ingredients} />
      </Section>
      <Section title="Foto">
        <div className="flex items-start gap-3">
          {item.photoUrl && (
            <img src={item.photoUrl} alt={item.name} className="h-[64px] w-[64px] shrink-0 rounded-xl object-cover" />
          )}
          <Field label="Indirizzo della foto" className="min-w-0 flex-1">
            <BlurText
              value={item.photoUrl ?? ""}
              disabled={disabled}
              placeholder="https://"
              onCommit={(text) => onSave("photoUrl", { photoUrl: text === "" ? null : text })}
            />
          </Field>
        </div>
        <FieldError message={errors.photoUrl} />
      </Section>
      <Section title="Catena del freddo">
        <Segmented
          options={COLD}
          value={item.cold ?? "none"}
          disabled={disabled}
          onChange={(value) => onSave("cold", { cold: value === "none" ? null : value })}
          ariaLabel="Catena del freddo"
          className="grid w-full grid-cols-2"
        />
        <FieldError message={errors.cold} />
      </Section>
      {item.isDrink && (
        <Section title="Produttore e origine">
          <div className="grid grid-cols-2 gap-x-3 gap-y-3">
            {WINE_FIELDS.map(({ key, label, wide }) => (
              <Field key={key} label={label} className={wide ? "col-span-2" : undefined}>
                <BlurText
                  value={wine?.[key] ?? ""}
                  disabled={disabled}
                  onCommit={(text) => onSave("wine", { wine: withWineField(wine, key, text) })}
                />
              </Field>
            ))}
          </div>
          <FieldError message={errors.wine} />
        </Section>
      )}
    </div>
  );
}
